import React, { useMemo, useRef, useState, useLayoutEffect, useEffect, useCallback } from 'react'
import { useQuery } from 'react-query'
import { TextField, MenuItem } from '@material-ui/core'
import ListTable from '../ListTable'
import WellborePipeDiagram from './WellborePipeDiagram'
import BhpPumpingScheduleTable from './BhpPumpingScheduleTable'
import SegmentedSwitch from '../SegmentedSwitch'
import LiveJobSignalrBhp from '../SignalrSubscribes/LiveJobSignalrBhp.component'
import DmsService from 'Services/DMS/dms-api.service'

const TABS = [
    { key: 'data', label: 'BHP Data' },
    { key: 'design', label: 'Design' },
]

const deviationColumns = [
    { Header: 'MD', accessor: 'md' },
    { Header: 'TVD', accessor: 'tvd' },
    { Header: 'Inc', accessor: 'inc' },
    { Header: 'Azi', accessor: 'azi' },
    { Header: 'NS', accessor: 'ns' },
    { Header: 'EW', accessor: 'ew' },
]

const tubularColumns = [
    { Header: 'Top MD', accessor: 'topMD' },
    { Header: 'Bottom MD', accessor: 'bottomMD' },
    { Header: 'Inner Diameter', accessor: 'innerDiameter' },
    { Header: 'Outer Diameter', accessor: 'outerDiameter' },
    { Header: 'Weight', accessor: 'weight' },
    { Header: 'Casing Inner Diameter', accessor: 'casingInnerDiameter' },
    { Header: 'Casing Outer Diameter', accessor: 'casingOuterDiameter' },
    { Header: 'Casing Weight', accessor: 'casingWeight' },
]

const injectionPointsColumns = [
    { Header: 'Stage', accessor: 'zone' },
    { Header: 'Top MD', accessor: 'topMD' },
    { Header: 'Bottom MD', accessor: 'bottomMD' },
    { Header: 'Nr Of Perforation', accessor: 'nrOfPerforation' },
    { Header: 'Perforation Diameter', accessor: 'perforationDiameter' },
    { Header: 'Phasing', accessor: 'phasing' },
    { Header: 'Perforation Efficiency', accessor: 'perforationEfficiency' },
]

const round = (n, digits = 1) => {
    if (n === null || n === undefined || Number.isNaN(n)) return '—'
    const factor = Math.pow(10, digits)
    return Math.round((n + Number.EPSILON) * factor) / factor
}

const Wellbore = ({ data, job, theme }) => {

    const deviationData = useMemo(() => data?.deviationData || [], [data])
    const tubularData = useMemo(() => data?.tubularData || [], [data])
    const injectionPoints = useMemo(() => data?.injectionPoints || [], [data])

    const [tab, setTab] = useState('data')
    const [bhpData, setBhpData] = useState(null)
    const handleBhpData = useCallback(incoming => {
        if (incoming) setBhpData(incoming)
    }, [])
    const isDataTab = tab === 'data'

    const containerRef = useRef(null)
    const leftColRef = useRef(null)
    const [leftHeight, setLeftHeight] = useState(null)
    const [isStacked, setIsStacked] = useState(false)
    // mobile: the diagram switches to the compact mode and sizes itself
    const [isNarrow, setIsNarrow] = useState(false)

    useLayoutEffect(() => {
        const el = leftColRef.current
        if (!el || typeof ResizeObserver === 'undefined') return undefined
        const observer = new ResizeObserver(entries => {
            for (const entry of entries) {
                setLeftHeight(entry.contentRect.height)
            }
        })
        observer.observe(el)
        return () => observer.disconnect()
    }, [])

    useLayoutEffect(() => {
        const el = containerRef.current
        if (!el || typeof ResizeObserver === 'undefined') return undefined
        // Below this width the two columns wrap onto separate rows (see flexWrap
        // below), so the right card should size itself naturally instead of being
        // forced to match the left column's height.
        const observer = new ResizeObserver(entries => {
            for (const entry of entries) {
                setIsStacked(entry.contentRect.width < 900)
                setIsNarrow(entry.contentRect.width < 520)
            }
        })
        observer.observe(el)
        return () => observer.disconnect()
    }, [])

    const zonesSignature = useMemo(
        () => injectionPoints.map(p => `${p.zone}:${p.topMD}:${p.bottomMD}`).join('|'),
        [injectionPoints],
    )

    const { data: zoneSummariesResponse } = useQuery(
        ['wellbore-zone-summaries', job?.id, zonesSignature],
        () => DmsService.getWellboreZoneSummaries(job.id),
        {
            enabled: !!job?.id,
            refetchOnWindowFocus: false,
        },
    )

    const zoneSummaries = useMemo(() => {
        const list = zoneSummariesResponse?.data
        return Array.isArray(list) ? list : []
    }, [zoneSummariesResponse])

    const [activeZone, setActiveZone] = useState(null)

    useEffect(() => {
        if (!zoneSummaries.length) {
            setActiveZone(null)
            return
        }
        setActiveZone(prev => {
            if (prev != null && zoneSummaries.some(z => z.zone === prev)) return prev
            return zoneSummaries[0].zone
        })
    }, [zoneSummaries])

    const activeZoneSummary = useMemo(
        () => zoneSummaries.find(z => z.zone === activeZone) || null,
        [zoneSummaries, activeZone],
    )

    const liveZone = isDataTab ? bhpData?.currentZone : null
    const activeInjectionPoint = useMemo(
        () => (liveZone != null && injectionPoints.find(p => p.zone === liveZone))
            || injectionPoints.find(p => p.zone === activeZone) || null,
        [injectionPoints, activeZone, liveZone],
    )

    const designLeftColumn = (
        <>
                <div className="wellboreCard">
                    <h4 className="wellboreCardTitle">Deviation Data</h4>
                    <ListTable
                        columns={deviationColumns}
                        data={deviationData}
                        tableClass="bordered wellboreTable"
                        enableSearch={false}
                        enablePagination={false}
                    />
                </div>
                <div className="wellboreCard">
                    <h4 className="wellboreCardTitle">Tubular Data</h4>
                    <ListTable
                        columns={tubularColumns}
                        data={tubularData}
                        tableClass="bordered wellboreTable"
                        enableSearch={false}
                        enablePagination={false}
                    />
                </div>
                <div className="wellboreCard">
                    <h4 className="wellboreCardTitle">Injection Points</h4>
                    <ListTable
                        columns={injectionPointsColumns}
                        data={injectionPoints}
                        tableClass="bordered wellboreTable"
                        enableSearch={false}
                        enablePagination={false}
                    />
                </div>
        </>
    )

    const dataLeftColumn = (
        <div className="wellboreCard bhpFill">
            <h4 className="wellboreCardTitle">Pumping Schedule</h4>
            <BhpPumpingScheduleTable steps={bhpData?.steps || []} />
        </div>
    )

    const bhpSummary = bhpData && (
        <div className="wellboreZoneSummaryBar">
            <span><strong>Step:</strong> {bhpData.currentStep ?? '—'}</span>
            <span><strong>Stage:</strong> {bhpData.currentZone ?? '—'}</span>
            <span><strong>Pipe volume:</strong> {round(bhpData.totalPipeVolume, 1)} {bhpData.volumeUnit || 'm³'}</span>
            <span><strong>Annulus volume:</strong> {round(bhpData.totalAnnulusVolume, 1)} {bhpData.volumeUnit || 'm³'}</span>
        </div>
    )

    return (
        <div>
        {job?.id && isDataTab && <LiveJobSignalrBhp jobId={job.id} onIncomingData={handleBhpData} />}
        <div className="wellboreTabs">
            <SegmentedSwitch
                options={TABS}
                value={tab}
                onChange={setTab}
            />
        </div>
        <div ref={containerRef} style={{ display: 'flex', gap: 20, alignItems: isDataTab ? 'stretch' : 'flex-start', flexWrap: 'wrap' }}>
            <div ref={leftColRef} style={{ flex: isDataTab ? '1 1 520px' : '1 1 420px', minWidth: 'min(320px, 100%)' }}>
                {isDataTab ? dataLeftColumn : designLeftColumn}
            </div>
            <div className="wellboreCard" style={{
                flex: '1 1 560px',
                minWidth: 'min(460px, 100%)',
                maxWidth: 'min(640px, 100%)',
                boxSizing: 'border-box',
                display: 'flex',
                flexDirection: 'column',
                padding: 0,
                overflow: 'hidden',
                height: isNarrow
                    ? undefined
                    : isDataTab
                    ? 'max(680px, calc(100vh - 260px))'
                    : ((!isStacked && leftHeight) ? `${leftHeight}px` : undefined),
            }}>
                <div style={{ padding: '14px 16px 0' }}>
                    <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap', marginBottom: 12 }}>
                        <h4 className="wellboreCardTitle" style={{ margin: 0 }}>Wellbore</h4>
                    </div>
                    {isDataTab && bhpSummary}
                    {
                        !isDataTab && zoneSummaries.length > 0 && (
                            <div className="wellboreZoneSummaryBar">
                                <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                                    <strong>Stage:</strong>
                                    <TextField
                                        select
                                        variant="outlined"
                                        margin="none"
                                        className="wellboreZoneSelect"
                                        value={activeZone ?? ''}
                                        onChange={(e) => setActiveZone(Number(e.target.value))}
                                    >
                                        {
                                            zoneSummaries.map(z => (
                                                <MenuItem key={z.zone} className="filterMenuItem" value={z.zone}>{z.zone}</MenuItem>
                                            ))
                                        }
                                    </TextField>
                                </div>
                                {
                                    activeZoneSummary && (
                                        <>
                                            <span><strong>Tubing volume:</strong> {round(activeZoneSummary.tubingVolume, 1)} m³</span>
                                            <span><strong>Annulus volume:</strong> {round(activeZoneSummary.annulusVolume, 1)} m³</span>
                                            <span><strong>Tubing weight:</strong> {round(activeZoneSummary.tubingWeight, 0)} kg</span>
                                        </>
                                    )
                                }
                            </div>
                        )
                    }
                </div>
                <div style={isNarrow ? undefined : { flex: '1 1 auto', minHeight: isStacked ? 320 : 0 }}>
                    <WellborePipeDiagram
                        deviationData={deviationData}
                        tubularData={tubularData}
                        injectionPoints={injectionPoints}
                        activeInjectionPoint={activeInjectionPoint}
                        fluidSegments={isDataTab ? (bhpData?.fluidSegments || []) : null}
                        theme={theme}
                        compact={isNarrow}
                    />
                </div>
            </div>
        </div>
        </div>
    )
}

export default Wellbore
